import { NextAuthenticatedPage } from 'next';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import React, {
  useEffect,
  useMemo,
  useLayoutEffect,
  useState,
  useRef,
} from 'react';
import { Set } from 'immutable';
import useInitializeLastUsedAvatar from '@avatar/utils/studio/useInitializeLastUsedAvatar';
import AvatarProvider from '@avatar/context/AvatarProvider';
import { Box, CircularProgress } from '@mui/material';
import { useActorsWithFiltersQuery } from '@avatar/graphql/filterAvatars/operation.generated';
import useReplacementsLoad from '@replacements/core/useReplacementsLoad';
import useInitializeProjectReplacements from '@replacements/hooks/projectReplacements/useInitializeProjectReplacements';
import { ReplacementsLibraryProvider } from '@replacements/context/ReplacementsLibraryContext';

import { aggregateReplacementsByWord } from '@replacements/utils/aggregateReplacementsByWord';
import { ReplacementsDictionaryProvider } from '@replacements/context/ReplacementsDictionaryContext';
import useProjectReplacements from '@replacements/core/useProjectReplacements';
import type { Actor } from '@avatar/types';
import useInitializeSectionVoices from '@avatar/utils/studio/useInitializeSectionVoices';
import { useEffectiveStudioLimit } from 'components/ScriptStudio/Sections/hooks/useProjectSections';
import { EditorBridgeProvider } from 'components/ScriptStudio/Editor/context/EditorBridgeContext';
import { resetScriptStudioState } from 'store/ScriptStudioSlice';
import { useSectionsQuery } from 'components/ScriptStudio/Sections/operation.generated';
import ScriptStudioError from 'components/ScriptStudio/Errors/ScriptStudioError';
import LegacyProjectBanner from 'components/Studio/legacy/LegacyProjectBanner';
import Centered from 'components/PageLayout/Centered';
import {
  AvatarOrderByOrder,
  Project,
  StudioProjectVersion,
  User,
} from '../../../../server/graphql/__generated.types';
import useActiveActor from '../../../components/avatar/hooks/useActiveActor';
import ErrorContent from '../../../components/ErrorContent';
import Page from '../../../components/Page';
import { useUserProjectQuery } from '../../../components/Projects/operation.generated';
import { ProjectAccessProvider } from '../../../context/projectAccess/ProjectAccessContext';
import { withSubscribedUser } from '../../../lib/withAuthentication';
import {
  getAvatarFromProjectClips,
  sortAvatarsByClipOrder,
} from '../../../components/avatar/utils/actor';
import useProjectEntityChanged from '../../../components/Projects/Listing/NewProjectsList/useProjectEntityChangedSubscription';
import useProjectAccessEntityChanged from '../../../components/Projects/Listing/NewProjectsList/useProjectAccessEntityChangedSubscription';
import useSelectedPreviewActor from '../../../components/avatar/hooks/useSelectedPreviewActor';
import { usePersistentClipStateAndActions } from '../../../components/StudioClipUserList/hooks/useStudioClips/common';
import storage from '../../../util/storage';
import { StudioClipPreviewListProvider } from '../../../context/previewClip/StudioPreviewClipContext';

import useStudioClips from '../../../components/StudioClipUserList/hooks/useStudioClips';
import { useEditorCharacterLimit } from '../../../context/editor/graphql.queries';
import {
  EditMode,
  setDefaultActorFromVoicesPage,
  setEditorState,
} from '../../../store/EditorSlice';
import { useAppDispatch } from '../../../store/hooks';
import {
  setLibrary,
  setReplacementsState,
} from '../../../store/ReplacementsSlice';
import ProjectPageContent from './ProjectPageContent';
import {
  ID_STUDIO_DRAWER_RIGHT,
  StudioToolbarWrapper,
} from '../../../components/Studio/ToolbarDrawer/StudioToolbarWrapper';
import UserEventsProviders from '../../../layouts/utils/UserEventsProviders';
import StudioToolbarProvider from './StudioToolbarProvider';
import { StickyPortal } from '../../../components/Sticky';
import StudioProjectNavbar from '../../../components/Studio/Navbar/StudioProjectNavbar';
import { LayoutElementState } from '../../../components/BaseLayout/context/LayoutContext';
import useIsLegacyProjectViewOnly from '../../../components/Studio/legacy/useIsLegacyProjectViewOnly';
import {
  hideCommentsSidebar,
  resetCommentsSidebarState,
} from '../../../store/CommentsSlice';
import StudioProjectProvider, { SampleRate } from './StudioProjectProvider';
import { ProjectSearchProvider } from '../../../components/ScriptStudio/Search/ProjectSearchContext';
import ScriptFirstProjectAccessProvider from './ProjectAccessProvider';

const ScriptStudio = dynamic(
  () => import('components/ScriptStudio/ScriptStudio'),
  { ssr: false }
);

const ScriptStudioSidebar = dynamic(
  () => import('components/ScriptStudio/Sidebar/ScriptStudioSidebar'),
  { ssr: false }
);

const ProjectMenuBar = dynamic(
  () => import('components/ScriptStudio/ProjectMenuBar'),
  { ssr: false }
);

const OnboardingModal = dynamic(
  () =>
    import(
      '../../../components/Subscriptions/Onboarding/components/Modal/OnboardingModalPresenter'
    ),
  { ssr: false }
);

const DashboardLayout = dynamic(
  () => import('../../../layouts/dashboard/DashboardLayout'),
  { ssr: false }
);

export const STORAGE_KEY_EDITOR_INITIAL_HEIGHT = 'editor_initial_height';

/**
 * The studio/project page is wrapped in a context providor (EditorProvider) so that we can use that
 * context directly within the page component.
 */
export interface Avatar {
  id: string;
  avatarName: string;
}

// eslint-disable-next-line react/no-unused-prop-types
export interface StudioPageProps {
  project: Project;
  edit: EditMode;
  user: User;
  projectScopedActors: Actor[];
  actorId?: string;
  variantId?: string;
  variantVersion?: string;
}

/**
 * NOTE: Ideally we would break up some of the editor logic within this page component to reduce
 * it's size/complexity
 */
const StudioPage: NextAuthenticatedPage<StudioPageProps> = ({
  project,
  edit,
  user,
  projectScopedActors,
  actorId,
  variantId,
  variantVersion,
}) => {
  const [editorInitialHeight, setEditorInitialHeight] = useState<number | null>(
    null
  );
  const { data: userProjectQuery } = useUserProjectQuery({
    variables: { inputs: { projectId: project.id } },
    fetchPolicy: 'cache-only',
  });
  const projectNameFromUserQuery = userProjectQuery?.user?.project?.name;
  const reactiveProjectName = useMemo(
    () => projectNameFromUserQuery || project.name,
    [projectNameFromUserQuery, project]
  );

  const projectVersionFromUserQuery =
    userProjectQuery?.user?.project?.studioVersion;
  const reactiveProjectVersion = useMemo(
    () => projectVersionFromUserQuery || project.studioVersion,
    [projectVersionFromUserQuery, project]
  );

  useProjectEntityChanged(project.id, null);
  useProjectAccessEntityChanged(project.id, null);

  /**
   * Actor & Editor actions/states
   */
  const {
    activeActor,
    activeClipActor,
    activeActorVariantVersion,
    setActiveActor,
    setActiveActorVariant,
  } = useActiveActor(projectScopedActors[0], project.studioVersion);

  const [{ clips }] = useStudioClips(project.id);
  const avatars = useMemo(() => {
    return sortAvatarsByClipOrder(project.id, projectScopedActors, clips ?? []);
  }, [clips, project.id, projectScopedActors]);
  const defaultAvatarAndVariant = useMemo(() => {
    return getAvatarFromProjectClips(project.id, clips, avatars);
  }, [avatars, clips, project.id]);
  const [lastProjectId, setLastProjectId] = useState<string>();
  const hasAppliedVoicesPageAvatarRef = useRef(false);

  /**
   * Determine the default avatar to use in project based on project clips
   */
  useEffect(() => {
    if (actorId && variantId && !hasAppliedVoicesPageAvatarRef.current) {
      const foundAvatar = avatars.find(avatar => avatar.id === Number(actorId));
      const foundVariant =
        foundAvatar &&
        foundAvatar.variants.find(variant => variant.id === Number(variantId));

      if (foundAvatar && foundVariant) {
        setActiveActor(foundAvatar, foundVariant, variantVersion);
        hasAppliedVoicesPageAvatarRef.current = true;
        setLastProjectId(project.id);
        return;
      }
    }

    if (project.id !== lastProjectId && defaultAvatarAndVariant) {
      const { avatar, activeVariant } = defaultAvatarAndVariant;
      setActiveActor(avatar, activeVariant, variantVersion);
      setLastProjectId(project.id);
    }
  }, [
    actorId,
    avatars,
    defaultAvatarAndVariant,
    lastProjectId,
    project.id,
    setActiveActor,
    variantId,
    variantVersion,
  ]);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (activeActor) {
      dispatch(
        setDefaultActorFromVoicesPage({
          actorId: activeActor.actor.id.toString(),
          variantId: activeActor.variant.id.toString(),
          ttsVersion: variantVersion ?? activeActor.version,
        })
      );
    }
  }, [activeActor, dispatch, variantVersion]);

  const { setSelectedPreviewActorIfDifferent } = useSelectedPreviewActor();

  const { setSelectedClipIds, setSearch } = usePersistentClipStateAndActions();
  // reset properties when switching projects
  useLayoutEffect(() => {
    setSelectedClipIds(Set<string>());
    setSearch('');
  }, [project.id, setSearch, setSelectedClipIds]);

  useEffect(() => {
    setSelectedPreviewActorIfDifferent(activeActor);
  }, [activeActor, setSelectedPreviewActorIfDifferent]);

  // safe to cast (if ability was undefined, StudioPageWrapper would have rendered error message)
  const ability = project?.projectAccess?.viewerAccessRole
    ?.permissions as string;

  const abilityFromUserQuery = userProjectQuery?.user?.project?.projectAccess
    ?.viewerAccessRole?.permissions as string;
  const memoizeAbility = useMemo(
    () => (abilityFromUserQuery !== '' && abilityFromUserQuery) || ability,
    [abilityFromUserQuery, ability]
  );

  useEffect(() => {
    if (!storage._config.scopedKey) {
      storage._config.scopedKey = user.id;
    }

    const retrieveInitialHeight = () => {
      const initialHeight = storage.getItem(
        storage.getScopedKey(STORAGE_KEY_EDITOR_INITIAL_HEIGHT)
      );

      if (typeof initialHeight === 'string') {
        const parsedHeight = parseInt(initialHeight, 10);

        if (!Number.isNaN(parsedHeight)) {
          setEditorInitialHeight(parsedHeight);
        }
      }
    };

    retrieveInitialHeight();
  }, [user]);

  const contentProps = {
    activeActor,
    activeActorVariantVersion,
    activeClipActor,
    edit,
    editorInitialHeight,
    project,
    projectScopedActors,
    reactiveProjectName,
    reactiveProjectVersion,
    setActiveActor,
    setActiveActorVariant,
  };

  return (
    <ProjectAccessProvider
      ability={memoizeAbility || JSON.stringify({ A: [] })}
    >
      <StudioClipPreviewListProvider>
        <LegacyProjectBanner project={project} />
        <Centered>
          <ProjectPageContent {...contentProps} />
        </Centered>
      </StudioClipPreviewListProvider>
    </ProjectAccessProvider>
  );
};

const StudioWrapper: NextAuthenticatedPage<Partial<StudioPageProps>> = ({
  project,
  edit = EditMode.SCRIPT,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const characterLimitData = useEditorCharacterLimit();

  useEffect(() => {
    if (project?.id) {
      dispatch(
        setEditorState({
          characterLimit: characterLimitData,
          projectId: project?.id || null,
        })
      );
      dispatch(hideCommentsSidebar());
    }
  }, [characterLimitData, dispatch, project?.id]);

  const ability = project?.projectAccess?.viewerAccessRole?.permissions;

  const [replacements, replacementsLoading] = useInitializeProjectReplacements(
    project?.id,
    project?.studioVersion
  );

  useEffect(() => {
    dispatch(
      setReplacementsState({
        replacementsLibraryLoading: replacementsLoading,
      })
    );
  }, [replacementsLoading, dispatch]);

  useEffect(() => {
    dispatch(
      setLibrary({
        library: replacements,
      })
    );
  }, [dispatch, replacements]);

  return (
    <Page title="Studio" id="page-studio" dataE2E="page-studio">
      <StickyPortal />
      {project && ability ? (
        <StudioPage
          project={project}
          edit={edit}
          actorId={props.actorId}
          variantId={props.variantId}
          variantVersion={props.variantVersion}
          {...props}
          projectScopedActors={props.projectScopedActors ?? []}
        />
      ) : (
        <Centered>
          <ErrorContent message="Project not found." />
        </Centered>
      )}
      <OnboardingModal />
    </Page>
  );
};

const ScriptFirstStudioPage: NextAuthenticatedPage<StudioPageProps> = (
  props: StudioPageProps
) => {
  const {
    project: initialPropsProject,
    projectScopedActors: projectAvatars,
    actorId,
    variantId,
    variantVersion,
  } = props;
  const dispatch = useAppDispatch();
  const sidebarRef = useRef<HTMLDivElement>(null);
  // required for fresh project data, project from getInitialProps only updates on refresh
  const { data } = useUserProjectQuery({
    variables: { inputs: { projectId: initialPropsProject.id } },
    fetchPolicy: 'cache-only',
  });
  const project = data?.user?.project ?? initialPropsProject;

  useInitializeLastUsedAvatar(project.id);
  useInitializeSectionVoices({ projectId: project.id });

  // When leaving the page, clear project related redux state
  useEffect(() => {
    return () => {
      dispatch(resetScriptStudioState());
      dispatch(resetCommentsSidebarState());
    };
  }, [dispatch]);

  return (
    <Page title="Studio" id="page-studio">
      <AvatarProvider projectAvatars={projectAvatars}>
        <StudioProjectProvider
          projectId={project.id}
          projectName={project.name}
          studioVersion={project.studioVersion}
          projectAvatars={projectAvatars}
          projectAccess={project.projectAccess.viewerAccessRole?.id}
          sampleRate={project.sampleRate ?? SampleRate.Hz24000}
          defaultPauseLength={project.defaultPauseLength ?? null}
          driveId={project.rootDriveId ?? project.driveId ?? ''}
          projectVoices={project.voices.map(v => v.id) ?? []}
        >
          <EditorBridgeProvider>
            <ProjectSearchProvider projectId={project.id}>
              <DashboardLayout
                toolbar={<ProjectMenuBar project={project} />}
                rightRail={
                  <aside
                    aria-label="Configuration"
                    // needed to avoid triggering replacements dictionary clickaway
                    id={ID_STUDIO_DRAWER_RIGHT}
                    style={{ height: '100%' }}
                    ref={sidebarRef}
                  >
                    {/* This 100% height is important for the click outside listener to work. Consider moving ref to sidebar parent */}
                    <ScriptStudioSidebar />
                  </aside>
                }
                header={
                  <StudioProjectNavbar project={project} showActions={false} />
                }
                defaultRightRailVisibility={LayoutElementState.OPEN}
              >
                <LegacyProjectBanner project={project} />
                <ScriptStudio
                  sidebarRef={sidebarRef}
                  project={project}
                  voicesPageAvatarId={actorId}
                  voicesPageAvatarVariantId={variantId}
                  voicesPageAvatarVariantVersion={variantVersion}
                />
              </DashboardLayout>
            </ProjectSearchProvider>
          </EditorBridgeProvider>
        </StudioProjectProvider>
      </AvatarProvider>
    </Page>
  );
};

const StudioPageWrapper: NextAuthenticatedPage<Partial<StudioPageProps>> = ({
  user,
  ...props
}) => {
  const router = useRouter();
  const projectIdFromQuery = router?.query?.projectId as string;
  const actorId = router?.query?.actorId as string | undefined;
  const variantId = router?.query?.variantId as string | undefined;
  const variantVersion = router?.query?.variantVersion as string | undefined;
  const edit = (router?.query?.edit as EditMode) || EditMode.SCRIPT;
  const effectiveLimit = useEffectiveStudioLimit();

  const { data: userProjectData, loading: projectLoading } =
    useUserProjectQuery({
      variables: { inputs: { projectId: projectIdFromQuery } },
      skip: !projectIdFromQuery,
      fetchPolicy: 'cache-and-network',
    });

  const project = userProjectData?.user?.project;

  const projectVersion =
    project?.studioVersion === StudioProjectVersion.ScriptStudio
      ? StudioProjectVersion.V2
      : project?.studioVersion;

  const { data: actorsData, loading: actorsLoading } =
    useActorsWithFiltersQuery({
      variables: {
        inputs: {
          projectVersion,
          orderBy: AvatarOrderByOrder.NameAsc,
        },
      },
      skip: !projectVersion,
    });

  useSectionsQuery({
    variables: {
      inputs: { projectId: project?.id as string, limit: effectiveLimit },
    },
    skip:
      !project ||
      (project.studioVersion !== StudioProjectVersion.ScriptStudio &&
        project.studioVersion !== StudioProjectVersion.Universal),
  });

  // Prefetch project replacements to warm the Apollo cache
  useProjectReplacements({
    projectId: project?.id,
    projectStudioVersion: project?.studioVersion,
    fetchPolicy: 'cache-and-network',
  });
  const { replacementsLibrary } = useReplacementsLoad({
    projectStudioVersion: project?.studioVersion,
    fetchPolicy: 'cache-and-network',
    skip: !project,
  });

  const mappedLibrary =
    replacementsLibrary && aggregateReplacementsByWord(replacementsLibrary);

  const isLegacyViewOnly = useIsLegacyProjectViewOnly(project?.studioVersion);

  if (projectLoading || actorsLoading) {
    return (
      <DashboardLayout defaultRightRailVisibility={LayoutElementState.OPEN}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100vh',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout defaultRightRailVisibility={LayoutElementState.OPEN}>
        <ScriptStudioError />
      </DashboardLayout>
    );
  }

  const projectScopedActors = actorsData?.user?.actors ?? [];

  const studioPageProps: StudioPageProps = {
    ...props,
    user: user!,
    project: project as Project,
    projectScopedActors,
    actorId,
    variantId,
    variantVersion,
    edit,
  };

  if (
    project.studioVersion === StudioProjectVersion.ScriptStudio ||
    project.studioVersion === StudioProjectVersion.Universal
  ) {
    return (
      <ScriptFirstProjectAccessProvider project={project as Project}>
        <ReplacementsLibraryProvider library={mappedLibrary || []}>
          <ReplacementsDictionaryProvider>
            <ScriptFirstStudioPage {...studioPageProps} />
            <OnboardingModal />
          </ReplacementsDictionaryProvider>
        </ReplacementsLibraryProvider>
      </ScriptFirstProjectAccessProvider>
    );
  }

  return (
    <StudioProjectProvider
      projectId={project.id}
      projectName={project.name}
      studioVersion={project.studioVersion}
      projectAvatars={projectScopedActors}
      sampleRate={project.sampleRate ?? SampleRate.Hz24000}
      defaultPauseLength={project.defaultPauseLength ?? null}
      driveId={project.rootDriveId ?? project.driveId ?? ''}
    >
      <StudioToolbarProvider
        actor={projectScopedActors[0]}
        projectVersion={project?.studioVersion}
      >
        <ReplacementsLibraryProvider library={mappedLibrary || []}>
          <ReplacementsDictionaryProvider>
            <DashboardLayout
              defaultRightRailVisibility={
                isLegacyViewOnly
                  ? LayoutElementState.HIDDEN
                  : LayoutElementState.OPEN
              }
              rightRail={
                isLegacyViewOnly ? undefined : (
                  <StudioToolbarWrapper {...studioPageProps} />
                )
              }
              header={<StudioProjectNavbar project={project} />}
            >
              <StudioWrapper {...studioPageProps} />
            </DashboardLayout>
          </ReplacementsDictionaryProvider>
        </ReplacementsLibraryProvider>
      </StudioToolbarProvider>
    </StudioProjectProvider>
  );
};

// StudioPage is wrapped by DashboardLayout which has the UserEvents provider.
// Do the providers wrapping DashboardLayout need UserEvent context?
// StudioToolbarProvider accesses user context
StudioPageWrapper.Layout = ({ children }: React.PropsWithChildren) => (
  <UserEventsProviders>{children}</UserEventsProviders>
);

export default withSubscribedUser(StudioPageWrapper);
